import React from "react";
import {
    Box,
    Divider,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { IGuiaConsultaDto } from "../../entities/guias/IGuiaConsultaDto";
import { currencyFormat } from "../../blocks/utility";
import { traducirLabel, traducirMessage } from "../../blocks/traductor";

const useStyles = makeStyles(() =>
    createStyles({
        sectionTitle: {
            color: "#19754e",
            fontSize: "14px",
            fontWeight: 700,
            marginTop: "1.25em",
            marginBottom: "0.5em"
        },
        blockText: {
            fontSize: "14px",
            color: "rgba(0,0,0,0.87)",
            lineHeight: 1.5
        },
        tableSummary: {
            width: "100%",
            fontSize: "14px",
            "& td": { padding: "6px 0", verticalAlign: "top" },
            "& td.price": { textAlign: "right" as const, whiteSpace: "nowrap" as const }
        },
        tableSummaryRow: {
            borderBottom: "1px solid rgba(0, 0, 0, 0.12)"
        },
        totalBox: {
            backgroundColor: "#338462",
            color: "#fff",
            marginTop: "1em",
            padding: "16px 20px",
            borderRadius: 4
        },
        totalRow: { fontWeight: 700, fontSize: "18px" },
        mercanciaTable: {
            "& .MuiTableCell-root": {
                fontSize: "13px",
                padding: "6px 8px"
            }
        },
        estatus: {
            color: "#d32f2f",
            fontSize: "13px",
            fontWeight: 600
        }
    })
);

interface Props {
    consulta: IGuiaConsultaDto;
}

const GuiaConsultaResumenView = ({ consulta }: Props) => {
    const classes = useStyles();
    const tieneSeguro = consulta.esSeguroBasico === 1 || consulta.esSeguroMercancia === 1;
    const tieneObservaciones = !!consulta.observaciones?.trim();
    const tieneDescripcion = !!consulta.descripcionMercancia?.trim();

    return (
        <Box px={3} pb={3}>
            {consulta.estatusNombre && (
                <Typography className={classes.estatus} align="center" gutterBottom>
                    {consulta.estatusNombre}
                </Typography>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Typography className={classes.sectionTitle}>{traducirLabel(222)}</Typography>
                    <Typography className={classes.blockText}>{consulta.nombreRemitente}</Typography>
                    <Box mt={1}>
                        <Typography className={classes.blockText}>{consulta.direccionRemitente}</Typography>
                        {consulta.sucursalRemitenteNombre && (
                            <Typography className={classes.blockText}>{consulta.sucursalRemitenteNombre}</Typography>
                        )}
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Typography className={classes.sectionTitle}>{traducirLabel(86)}</Typography>
                    <Typography className={classes.blockText}>{consulta.nombreDestinatario}</Typography>
                    <Box mt={1}>
                        <Typography className={classes.blockText}>{consulta.direccionDestinatario}</Typography>
                        {consulta.sucursalDestinatarioNombre && (
                            <Typography className={classes.blockText}>{consulta.sucursalDestinatarioNombre}</Typography>
                        )}
                    </Box>
                </Grid>
            </Grid>

            <Divider style={{ margin: "24px 0" }} />

            <Typography className={classes.sectionTitle}>{traducirLabel(88)}</Typography>
            <TableContainer>
                <Table size="small" className={classes.mercanciaTable}>
                    <TableHead>
                        <TableRow>
                            <TableCell align="center">{traducirLabel(29)}</TableCell>
                            <TableCell align="center">{traducirLabel(111)}</TableCell>
                            <TableCell align="center">{traducirLabel(229)}</TableCell>
                            <TableCell align="center">{traducirLabel(174)}</TableCell>
                            <TableCell align="center">{traducirLabel(320)}</TableCell>
                            <TableCell align="center">{traducirLabel(230)}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(consulta.mercancias ?? []).map((m, idx) => (
                            <TableRow key={idx}>
                                <TableCell align="center">{m.cantidad}</TableCell>
                                <TableCell align="center">{m.embalajeNombre}</TableCell>
                                <TableCell align="center">{m.peso}</TableCell>
                                <TableCell align="center">{`${m.largo} x ${m.ancho} x ${m.alto}`}</TableCell>
                                <TableCell align="center">{m.volumen}</TableCell>
                                <TableCell align="center">{m.pesoFacturado}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {tieneDescripcion && (
                <Box mt={2}>
                    <Typography className={classes.sectionTitle}>{traducirLabel(83)}</Typography>
                    <Typography className={classes.blockText}>{consulta.descripcionMercancia}</Typography>
                </Box>
            )}

            {tieneSeguro && (
                <Box mt={2}>
                    <Typography className={classes.sectionTitle}>{traducirMessage(22)}</Typography>
                    <Typography className={classes.blockText}>
                        {consulta.esSeguroBasico === 1 ? traducirLabel(273) : traducirLabel(274)}
                        {consulta.esSeguroMercancia === 1 && consulta.valorMercancia != null && (
                            <> — {traducirLabel(314)}: {currencyFormat(Number(consulta.valorMercancia))}</>
                        )}
                    </Typography>
                </Box>
            )}

            {consulta.tieneRetornoDocumentos && (
                <Box mt={2}>
                    <Typography className={classes.sectionTitle}>{traducirLabel(278)}</Typography>
                    <Typography className={classes.blockText}>{traducirLabel(102)}</Typography>
                </Box>
            )}

            {tieneObservaciones && (
                <Box mt={2}>
                    <Typography className={classes.sectionTitle}>{traducirMessage(27)}</Typography>
                    <Typography className={classes.blockText}>{consulta.observaciones}</Typography>
                </Box>
            )}

            <Grid container spacing={3} style={{ marginTop: 16 }}>
                <Grid item xs={12} md={7} />
                <Grid item xs={12} md={5}>
                    <Typography className={classes.sectionTitle}>{traducirLabel(367)}</Typography>
                    <table className={classes.tableSummary}>
                        <tbody>
                            <tr className={classes.tableSummaryRow}>
                                <td>{traducirLabel(287)}</td>
                                <td className="price">{currencyFormat(consulta.subtotal)}</td>
                            </tr>
                            <tr className={classes.tableSummaryRow}>
                                <td>{traducirLabel(166)}</td>
                                <td className="price">{currencyFormat(consulta.iva)}</td>
                            </tr>
                            <tr className={classes.tableSummaryRow}>
                                <td>{traducirLabel(267)}</td>
                                <td className="price">{currencyFormat(consulta.retencion)}</td>
                            </tr>
                        </tbody>
                    </table>
                    <Box className={classes.totalBox}>
                        <table className={classes.tableSummary}>
                            <tbody>
                                <tr className={classes.totalRow}>
                                    <td>{traducirLabel(305)}</td>
                                    <td className="price">
                                        {currencyFormat(consulta.total)} {consulta.monedaNombre}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

export default GuiaConsultaResumenView;
